// frontend/src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage'; // ✅ NEW
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import AdminDashboard from './pages/AdminDashboard';

import MembersListPage from './pages/MembersListPage';
import AddMemberPage from './pages/AddMemberPage';
import MemberProfilePage from './pages/MemberProfilePage';
import EditMemberPage from './pages/EditMemberPage';
import ExitMemberWizard from './pages/ExitMemberWizard';
import MemberLoanStatusPage from './pages/MemberLoanStatusPage';

import MembersImportPage from './pages/MembersImportPage';

import ApplyLoanPage from './pages/ApplyLoanPage';
import LoanReviewPage from './pages/LoanReviewPage';

import MonthlyProcessingPage from './pages/MonthlyProcessingPage';
import ActivityLogPage from './pages/ActivityLogPage';
import ReportsPage from './pages/reportpage/ReportsPage';
import MemberStatementPage from './pages/MemberStatementPage';
import MergedMemberStatementPage from './pages/MergedMemberStatementPage';
import MonthlyMembersOverviewPage from './pages/MonthlyMembersOverviewPage';

import EnhancedHistoricalLoanImport from './pages/EnhancedHistoricalLoanImport';

import ManageSharesPage from './pages/ManageSharesPage';
import UsersPage from './pages/UsersPage';
import BulkMemberAccountsPage from './pages/BulkMemberAccountsPage';
import DevToolsPage from './pages/DevToolsPage';
import HowToPage from './pages/HowToPage';

// ================= MEMBER PORTAL PAGES =================
import MemberDashboard from './pages/member/MemberDashboard';
import MyProfilePage from './pages/member/MyProfilePage';
import MyLoansPage from './pages/member/MyLoansPage';
import MyStatementPage from './pages/member/MyStatementPage';
import RequestLoan from './pages/member/RequestLoan';

import SessionMonitor from './components/SessionMonitor';
import RoleSelectPage from './pages/RoleSelectPage';

import './UnifiedTheme.css';

// Scroll to top on route change
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

// Any logged-in user
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('hopeSaccoToken');
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

// Only admin or official can access admin routes
const AdminRoute = ({ children }) => {
  const token = localStorage.getItem('hopeSaccoToken');
  if (!token) return <Navigate to="/login" replace />;
  try {
    const user = JSON.parse(localStorage.getItem('hopeSaccoUser') || '{}');
    const role = user.role || '';
    if (role !== 'admin' && role !== 'official') {
      return <Navigate to="/member/dashboard" replace />;
    }
  } catch {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  return (
    <Router>
      <SessionMonitor />
      <ScrollToTop />
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/role-select" element={<RoleSelectPage />} />

          {/* ========================================
              MEMBER PORTAL ROUTES
              ======================================== */}
          <Route
            path="/member/dashboard"
            element={
              <ProtectedRoute>
                <MemberDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/profile"
            element={
              <ProtectedRoute>
                <MyProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/loans"
            element={
              <ProtectedRoute>
                <MyLoansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/statement"
            element={
              <ProtectedRoute>
                <MyStatementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/member/request-loan"
            element={
              <ProtectedRoute>
                <RequestLoan />
              </ProtectedRoute>
            }
          />

          {/* ========================================
              ADMIN ROUTES
              ======================================== */}
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          {/* Members */}
          <Route
            path="/admin/members"
            element={
              <AdminRoute>
                <MembersListPage />
              </AdminRoute>
            }
          />
          {/* Redirect /add to /new for backward compatibility */}
          <Route path="/admin/members/add" element={<Navigate to="/admin/members/new" replace />} />
          <Route
            path="/admin/members/new"
            element={
              <AdminRoute>
                <AddMemberPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/import"
            element={
              <AdminRoute>
                <MembersImportPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/:id/edit"
            element={
              <AdminRoute>
                <EditMemberPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/:id/exit"
            element={
              <AdminRoute>
                <ExitMemberWizard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/members/:id"
            element={
              <AdminRoute>
                <MemberProfilePage />
              </AdminRoute>
            }
          />

          {/* Loans */}
          <Route
            path="/admin/apply-loan"
            element={
              <AdminRoute>
                <ApplyLoanPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/loans/review/:memberId"
            element={
              <AdminRoute>
                <LoanReviewPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/member-loan-status/:memberId"
            element={
              <AdminRoute>
                <MemberLoanStatusPage />
              </AdminRoute>
            }
          />

          {/* Historical Loan Import */}
          <Route
            path="/admin/loans/import-historical"
            element={
              <AdminRoute>
                <EnhancedHistoricalLoanImport />
              </AdminRoute>
            }
          />

          {/* Reports */}
          <Route
            path="/admin/reports"
            element={
              <AdminRoute>
                <ReportsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/reports/member-statement"
            element={
              <AdminRoute>
                <MemberStatementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/reports/merged-statement"
            element={
              <AdminRoute>
                <MergedMemberStatementPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/reports/monthly-overview"
            element={
              <AdminRoute>
                <MonthlyMembersOverviewPage />
              </AdminRoute>
            }
          />

          {/* Monthly Processing */}
          <Route
            path="/admin/monthly-processing"
            element={
              <AdminRoute>
                <MonthlyProcessingPage />
              </AdminRoute>
            }
          />

          {/* Activity Log — unified audit log for all sources */}
          <Route
            path="/admin/activity-log"
            element={
              <AdminRoute>
                <ActivityLogPage />
              </AdminRoute>
            }
          />

          {/* Manage Shares */}
          <Route
            path="/admin/members/manage-shares"
            element={
              <AdminRoute>
                <ManageSharesPage />
              </AdminRoute>
            }
          />

          {/* Users */}
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <UsersPage />
              </AdminRoute>
            }
          />

          {/* Bulk Member Account Creation */}
          <Route
            path="/admin/bulk-member-accounts"
            element={
              <AdminRoute>
                <BulkMemberAccountsPage />
              </AdminRoute>
            }
          />

          {/* DevTools */}
          <Route
            path="/devtools"
            element={
              <AdminRoute>
                <DevToolsPage />
              </AdminRoute>
            }
          />

          {/* How To */}
          <Route
            path="/admin/how-to"
            element={
              <AdminRoute>
                <HowToPage />
              </AdminRoute>
            }
          />

          {/* Default */}
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
